import React, { useMemo } from 'react';
import { useAuth } from 'auth';
import { useStorageService, STORAGE_KEYS } from 'services/storage';

import { Button, Flex, Layout, Select } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useGetCustomerPortalUrlMutation } from 'graph/dist/generated';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faSignOut } from '@fortawesome/pro-light-svg-icons';
import { useTheme } from '../../theme';

import Loader from 'components/Loading/Loader';
import PlansList from 'components/Subscription/PlansList';
import { ApiAccessSection } from './ApiAccessSection';

import './Profile.sass';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
	APP_LANGUAGE_CODES,
	LANGUAGE_NATIVE_LABELS,
	type AppLanguageCode
} from '../../constants/appLanguages';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/hi';
import 'dayjs/locale/it';
import 'dayjs/locale/ja';
import 'dayjs/locale/pt';
import 'dayjs/locale/zh-cn';

const DAYJS_LOCALE_BY_APP_LANG: Record<string, string> = {
	de: 'de',
	en: 'en',
	es: 'es',
	fr: 'fr',
	hi: 'hi',
	it: 'it',
	ja: 'ja',
	pt: 'pt',
	zh: 'zh-cn'
};

interface Props {
	returnTo: string;
	paymentPublicKey: string;
	isSubscriber: boolean;
	subscriptionDetails: any;
}

dayjs.extend(relativeTime)

export const Profile = (props: Props) => {
	const history = useHistory();
	const { isLoading: loading, user, logout } = useAuth();
	const storage = useStorageService();
	const { t, i18n } = useTranslation();
	const { currentThemeId, availableThemes, setTheme } = useTheme();
	const [getCustomerPortalUrlMutation] = useGetCustomerPortalUrlMutation();

	// Set dayjs locale (codes app ≠ codes dayjs pour certaines langues, ex. zh → zh-cn)
	const baseI18nLang = i18n.language.split('-')[0];
	dayjs.locale(DAYJS_LOCALE_BY_APP_LANG[baseI18nLang] || baseI18nLang || 'en');

	const redirectToCustomerPortal = async () => {
		const result = await getCustomerPortalUrlMutation();
		console.log('result', result);
		document.location.href = result.data.getCustomerPortalUrl;
	};

	const activeLanguage = i18n.language;
	const activeLanguageBase = activeLanguage.split('-')[0];

	const languageSelectValue: AppLanguageCode = (
		APP_LANGUAGE_CODES as readonly string[]
	).includes(activeLanguageBase)
		? (activeLanguageBase as AppLanguageCode)
		: 'en';

	const languageOptions = useMemo(
		() =>
			APP_LANGUAGE_CODES.map((code) => {
				const native = LANGUAGE_NATIVE_LABELS[code];
				const inUiLang = t(`pages.profile.language.languages.${code}`);
				const label =
					inUiLang === native ? native : `${native} - ${inUiLang}`;
				return { value: code, label };
			}),
		[t, i18n.language]
	);

	const changeLanguage = (language: string) => {
		i18n.changeLanguage(language);
		storage.setItem(STORAGE_KEYS.LANGUAGE, language);
	};

	return (
		<Layout className="Profile">

			<Layout.Header>
				<Flex align="center" style={{ height: '55px' }}>

					<Button className="mr-1" shape="circle" onClick={() => {
						history.push('/projects');
					}}>
						<FontAwesomeIcon icon={faChevronLeft} />
					</Button>

					<h2 className="mb-0">{t('pages.profile.title')}</h2>

				</Flex>
			</Layout.Header>

			{loading ? (
				<Loader></Loader>
			) : (
				<Layout.Content className="pb-4">

					<div className="infos">
						{user && (
							<>
								<img src={user.picture} alt="Profile" />
								<div>
									<h2>{user.name}</h2>
									<p>{user.email}</p>
								</div>
							</>
						)}
					</div>

					<div className="language">
						<div className="title">
							{t('pages.profile.language.title')}
						</div>
						<div className="subtitle">
							{t('pages.profile.language.description')}
						</div>
						<div className="language-select-wrap">
							<Select<AppLanguageCode>
								className="language-select"
								value={languageSelectValue}
								options={languageOptions}
								onChange={(code) => changeLanguage(code)}
								popupMatchSelectWidth={false}
								aria-label={t('pages.profile.language.title')}
							/>
						</div>

					</div>


					<div className="ThemeSelector mt-3">
						<div className="title">
							{t('pages.profile.theme.title')}
						</div>
					</div>

					<>
						<div className="title mt-1">
							{availableThemes.map((theme) => (
								<Button
									className={`theme-item mr-05`}
									onClick={() => setTheme(theme.id)}
									type={currentThemeId === theme.id ? "primary" : undefined}
								>
									<div className="theme-item-name">
										{t(`theme.themes.${theme.id}`)}
									</div>
								</Button>
							))}</div>

					</>

					{!props.isSubscriber &&
						import.meta.env.VITE_APP_MODE !== 'corporate' ? (
						<>
							<div className="title mt-3">
								{t('pages.profile.messages.choose_plan')}
							</div>
							<div className="subtitle">
								{t('pages.profile.messages.unlock_immediately')}
							</div>
							<PlansList />
						</>
					) : (
						props.subscriptionDetails && (
							<div className="subscriptionDetails text-center m-auto mb-4">

								<div className="title mt-3">
									{t('pages.profile.messages.current_subscription')}
								</div>


								{props.subscriptionDetails.startAt && (
									<>
										<div className="mt-2">

											{dayjs
												.unix(
													props.subscriptionDetails
														.startAt
												)
												.format('DD/MM/YYYY')}
											{props.subscriptionDetails
												.endAt && (
													<>
														{' '}
														-{' '}
														{dayjs
															.unix(
																props
																	.subscriptionDetails
																	.endAt
															)
															.format('DD/MM/YYYY')}{' '}
														(
														{dayjs
															.unix(
																props
																	.subscriptionDetails
																	.endAt
															)
															.fromNow()}
														)
													</>
												)}
										</div>
										<div className="mt-4 mb-4">
											<Button
												onClick={
													redirectToCustomerPortal
												}
											>
												{t('pages.profile.actions.manage_subscription')}
											</Button>
										</div>
									</>
								)}
							</div>
						)
					)}
					<ApiAccessSection />

					<div
						className="mt-4"
						style={{ display: 'flex', justifyContent: 'center' }}
					>
						<Button
							onClick={(e) => {
								e.preventDefault();
								logout({ returnTo: props.returnTo });
							}}
						>
							<FontAwesomeIcon icon={faSignOut} /> &nbsp;
							{t('actions.sign_out')}
						</Button>
					</div>
				</Layout.Content>
			)}
		</Layout>
	);
};
